import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { warn } from '@ember/debug';
import type SessionService from 'ember-simple-auth/services/session';
import type { IntlService } from 'ember-intl';
import type Transition from '@ember/routing/transition';
import Intercom from '@intercom/messenger-js-sdk';
import ENV from 'dashboard/config/environment';
import { isDevelopingApp, isTesting, macroCondition } from '@embroider/macros';
import { chooseBestFitLocale } from '@showbie/utilities';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'dashboard/constants';
import type FeaturesService from '@showbie/feature-flags-ember/services/features';
import breakpoints from 'dashboard/breakpoints';
import type MediaService from 'ember-media-query/services/media';
import { formats } from 'dashboard/ember-intl-formats';

// https://www.npmjs.com/package/@intercom/messenger-js-sdk#installation
const INTERCOM_SETTINGS = {
  api_base: 'https://api-iam.intercom.io',
  app_id: ENV.intercom.app_id,
};

if (macroCondition(!isTesting())) {
  Intercom(INTERCOM_SETTINGS);
}

export default class ApplicationRoute extends Route {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare media: MediaService;
  @service declare session: SessionService;

  async beforeModel(transition: Transition): Promise<void> {
    super.beforeModel(transition);

    // define `ember-media-query` breakpoints
    this.media.setBreakpoints(breakpoints);

    // load feature flags
    await this.features.setup(ENV.featureFlags);
    // https://github.com/simplabs/ember-simple-auth/blob/master/guides/upgrade-to-v4.md
    await this.session.setup();

    this.setupIntl();
  }

  private setupIntl(): void {
    let locale = DEFAULT_LOCALE;
    if (!this.session.isAuthenticated) {
      // choose locale based on browser settings for non-authenticated users
      locale = chooseBestFitLocale(SUPPORTED_LOCALES);
    }

    // https://ember-intl.github.io/ember-intl/versions/v5.7.0/docs/quickstart#4-configure-ember-intl
    this.intl.setLocale([locale]);

    // https://github.com/ember-intl/ember-intl/pull/1972
    this.intl.setFormats(formats);

    // https://ember-intl.github.io/ember-intl/docs/services/intl-part-2#methods-set-on-missing-translation
    // on production, do not provide a missing translation replacement
    if (macroCondition(isDevelopingApp())) {
      this.intl.setOnMissingTranslation((key) => {
        warn(
          `[ember-intl] translation: "${key}" on locale: "${locale}" was not found.`,
          false,
          {
            id: 'ember-intl-missing-translation',
          },
        );
        return `Missing translation: "${key}"`;
      });
    }
  }
}
